import { z } from "zod";
import {
  BRIDGE_IDS,
  VESSEL_CORRIDORS,
  type EventPage,
  type LiveServerMessage,
  type PublicBridge,
  type PublicSnapshot,
  type PublicRoadTraffic,
  type PublicVesselDetail,
  type PublicVesselGroup,
  type PublicVesselSummary,
  type PublicWeatherOutlook,
} from "../../shared/contracts";

const nullableNumber = z.number().nullable();
const nullableString = z.string().nullable();
const nullableIsoString = z.iso.datetime({ offset: true }).nullable();
const publicVesselIdSchema = z.string().min(1).max(96).regex(/^[A-Za-z0-9_-]+$/);

export const publicBridgeSchema = z.strictObject({
  id: z.enum(BRIDGE_IDS),
  name: z.string(),
  shortName: z.string(),
  state: z.enum(["down", "up", "unknown"]),
  freshness: z.enum(["live", "delayed", "stale", "unknown"]),
  observedAt: nullableString,
  lastConfirmedAt: nullableString,
}) satisfies z.ZodType<PublicBridge>;

export const publicVesselSummarySchema = z.strictObject({
  id: publicVesselIdSchema,
  name: nullableString,
  type: z.enum([
    "tug",
    "tow",
    "cargo",
    "tanker",
    "sailboat",
    "yacht",
    "passenger",
    "fishing",
    "pilot",
    "generic",
  ]),
  corridor: z.enum(VESSEL_CORRIDORS),
  direction: z.enum(["inbound", "outbound", "holding", "unknown"]),
  sMeters: z.number(),
  bridgePhase: z.enum(["approaching", "waiting", "clearing"]),
  knownOpener: z.boolean(),
  progress: z.number().min(0).max(1),
  corridorProgress: z.number().min(0).max(1),
  distanceToBrickellMeters: z.number().nonnegative().nullable(),
  speedKnots: nullableNumber,
  courseDegrees: nullableNumber,
  etaMinutes: nullableNumber,
  observedAt: z.string(),
}) satisfies z.ZodType<PublicVesselSummary>;

export const publicVesselGroupSchema = z.strictObject({
  id: z.string().min(1).max(512).regex(/^[A-Za-z0-9_.-]+$/),
  kind: z.literal("likely_tow"),
  tugIds: z.array(publicVesselIdSchema).min(1),
  towIds: z.array(publicVesselIdSchema).min(1),
  observedAt: z.string(),
  memberOffsetsMeters: z.record(publicVesselIdSchema, z.number().finite().min(-500).max(500)).optional(),
}).superRefine((group, context) => {
  const tugIds = new Set(group.tugIds);
  const towIds = new Set(group.towIds);
  if (group.memberOffsetsMeters && (
    Object.keys(group.memberOffsetsMeters).length !== group.tugIds.length + group.towIds.length ||
    [...group.tugIds, ...group.towIds].some((id) => group.memberOffsetsMeters?.[id] === undefined)
  )) context.addIssue({ code: "custom", path: ["memberOffsetsMeters"], message: "offsets must match every member" });
  if (tugIds.size !== group.tugIds.length) {
    context.addIssue({ code: "custom", path: ["tugIds"], message: "tug ids must be unique" });
  }
  if (towIds.size !== group.towIds.length) {
    context.addIssue({ code: "custom", path: ["towIds"], message: "tow ids must be unique" });
  }
  if (group.tugIds.some((id) => towIds.has(id))) {
    context.addIssue({ code: "custom", path: ["towIds"], message: "tow members must be disjoint" });
  }
}) satisfies z.ZodType<PublicVesselGroup>;

export const publicVesselDetailSchema = publicVesselSummarySchema.extend({
  callSign: nullableString,
  imo: nullableString,
  destination: nullableString,
  lengthMeters: nullableNumber,
  beamMeters: nullableNumber,
  passages: z.array(z.strictObject({
    bridgeId: z.enum(BRIDGE_IDS),
    bridgeName: z.string(),
    crossedAt: z.string(),
  })),
}) satisfies z.ZodType<PublicVesselDetail>;

const publicVesselActivitySchema = z.strictObject({
  activeTracks: z.number().int().nonnegative(),
  approachingBridge: z.number().int().nonnegative(),
  status: z.enum(["receiving", "connected", "connecting", "reconnecting", "unavailable", "disabled"]),
  issue: z.enum([
    "upgrade_timeout",
    "upgrade_failed",
    "upgrade_rejected",
    "upgrade_accept_failed",
    "subscription_timeout",
    "subscription_send_failed",
    "stream_idle",
    "service_error",
    "decode_failed",
    "unsupported_frame",
    "socket_closed",
    "socket_error",
  ]).nullable(),
  freshness: z.enum(["live", "delayed", "stale", "unknown"]),
  updatedAt: nullableString,
});

export const publicWeatherOutlookSchema = z.strictObject({
  condition: z.enum([
    "clear",
    "mainly_clear",
    "partly_cloudy",
    "overcast",
    "fog",
    "drizzle",
    "freezing_drizzle",
    "rain",
    "freezing_rain",
    "snow",
    "showers",
    "thunderstorm",
    "unknown",
  ]),
  conditionsValidAt: nullableIsoString,
  isDay: z.boolean().nullable(),
  temperatureF: nullableNumber,
  precipitationRateMmPerHour: z.number().nonnegative().nullable(),
  cloudCoverPercent: z.number().min(0).max(100).nullable(),
  cloudCoverLowPercent: z.number().min(0).max(100).nullable(),
  cloudCoverMidPercent: z.number().min(0).max(100).nullable(),
  cloudCoverHighPercent: z.number().min(0).max(100).nullable(),
  windSpeedMph: z.number().nonnegative().nullable(),
  windDirectionDegrees: z.number().min(0).max(360).nullable(),
  windGustMph: z.number().nonnegative().nullable(),
  directNormalIrradianceWm2: z.number().nonnegative().nullable(),
  diffuseRadiationWm2: z.number().nonnegative().nullable(),
  state: z.enum(["dry", "rain_expected", "rain_window_now", "unavailable"]),
  windowStartsAt: nullableIsoString,
  windowEndsAt: nullableIsoString,
  amountMm: z.number().nonnegative().nullable(),
  rainChancePercent: z.number().min(0).max(100).nullable(),
  freshness: z.enum(["live", "delayed", "stale", "unknown"]),
  updatedAt: nullableIsoString,
}) satisfies z.ZodType<PublicWeatherOutlook>;

export const publicRoadTrafficSchema = z.strictObject({
  source: z.enum(["tomtom", "modeled"]),
  condition: z.enum(["free_flow", "moderate", "heavy", "severe", "unavailable"]),
  congestionPercent: z.number().min(0).max(100).nullable(),
  speedMph: z.number().min(0).max(225).nullable(),
  freeFlowSpeedMph: z.number().min(0).max(225).nullable(),
  observedSegments: z.number().int().nonnegative(),
  roads: z.array(z.strictObject({
    pathId: z.string().min(1),
    condition: z.enum(["free_flow", "moderate", "heavy", "severe"]),
    congestionPercent: z.number().min(0).max(100),
    speedMph: z.number().min(0).max(225).nullable(),
    freeFlowSpeedMph: z.number().min(0).max(225).nullable(),
    observedSegments: z.number().int().positive(),
  })),
  freshness: z.enum(["live", "delayed", "stale", "unknown"]),
  updatedAt: nullableIsoString,
}) satisfies z.ZodType<PublicRoadTraffic>;

export const publicSnapshotSchema = z.strictObject({
  version: z.literal("v1"),
  dataMode: z.enum(["demo", "live"]),
  sequence: z.number().int().nonnegative(),
  generatedAt: z.string(),
  experience: z.strictObject({ livingRiverEnabled: z.boolean() }),
  forecast: z.strictObject({
    state: z.enum(["clear", "likely", "open", "unavailable"]),
    horizonMinutes: z.literal(30),
    openingWindow: z.strictObject({
      earliestMinutes: z.number().int().min(0).max(180),
      latestMinutes: z.number().int().min(0).max(180),
    }).nullable(),
    confidencePercent: z.number().int().min(0).max(100).nullable(),
    reason: z.strictObject({
      kind: z.enum(["vessel_approach", "scheduled_river_transit", "upstream_progress"]),
      vesselName: nullableString,
    }).nullable(),
    freshness: z.enum(["live", "delayed", "stale", "unknown"]),
    updatedAt: z.string(),
  }),
  bridges: z.array(publicBridgeSchema),
  weather: publicWeatherOutlookSchema,
  roadTraffic: publicRoadTrafficSchema,
  vesselActivity: publicVesselActivitySchema,
  vessels: z.array(publicVesselSummarySchema),
  vesselGroups: z.array(publicVesselGroupSchema),
}) satisfies z.ZodType<PublicSnapshot>;

export const eventPageSchema = z.strictObject({
  events: z.array(z.strictObject({
    id: z.string(),
    bridgeId: z.enum(BRIDGE_IDS),
    bridgeName: z.string(),
    state: z.enum(["down", "up"]),
    occurredAt: z.string(),
  })),
  nextCursor: nullableString,
  hasMore: z.boolean(),
}) satisfies z.ZodType<EventPage>;

export const liveServerMessageSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("snapshot"), snapshot: publicSnapshotSchema }),
  z.strictObject({
    type: z.literal("update"),
    sequence: z.number().int().nonnegative(),
    snapshot: publicSnapshotSchema,
  }),
  z.strictObject({
    type: z.literal("resync"),
    expectedSequence: z.number().int().nonnegative(),
  }),
]) satisfies z.ZodType<LiveServerMessage>;
